// Artifact composer chrome: the builder-model picker, the kind chip and menu, the
// Skills menu and the image attachment (T1-UI1). Namespace `artifact-composer`.
//
// Chrome only. Model names, hints, regions and prices come from the model
// registry through the view model, and kind and skill labels come from their
// registries; none of them is written here. All 24 app languages are written out below: `en` and `sv` are the source, the rest
// are translated chrome copy (placeholders and the generic word "Artifact" preserved).
// See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  pickerLabel: 'Build with',
  pickerLoading: 'Loading models',
  pickerNone: 'No model available',
  pickerDenied: 'You can’t choose a model here',
  pickerError: 'Models could not be loaded',
  pickerBlocked: 'Choosing a model is paused',
  pickerUnavailable: 'Not available',
  pickerCurrent: 'Selected',
  pickerNextTurn: 'Applies from your next message.',
  pickerFootnote:
    'Remembered for your next builds. Your admin picks which models your company can use.',
  kindLabel: 'Artifact',
  kindMenuLabel: 'Kind of Artifact',
  kindSheetTitle: 'Kind',
  kindUnavailable: 'Not available yet',
  kindGroupApp: 'Interactive',
  kindGroupRead: 'Made to be read or sent',
  checkedTitle: 'Checked before I said done',
  checkedPassed: 'Passed',
  checkedFailed: 'Did not pass',
  checkedBlocked: 'Could not run',
  checkedStale: 'Out of date: the Artifact changed after this check',
  checkNow: 'Check',
  checkAgain: 'Check again',
  checkedAbsent: 'Not checked yet',
  checkedScreens: '{count} screens captured',
  checkedLimitations: 'What this check did not cover',
  checkedVersion: 'Version {version}',
  skillsLabel: 'Skills',
  skillsEmpty: 'No skills yet',
  skillsLoading: 'Loading skills',
  skillsError: 'Skills could not be loaded',
  skillsDenied: 'You can’t use Skills here',
  skillsAttached: 'Using this Skill',
  imageAttach: 'Attach an image',
  imageDrop: 'Drop an image to start from it',
  imageNotImage: 'That file is not an image.',
  imageStatus: 'Image {label}: {status}',
};

const sv: typeof en = {
  pickerLabel: 'Bygg med',
  pickerLoading: 'Läser in modeller',
  pickerNone: 'Ingen modell tillgänglig',
  pickerDenied: 'Du kan inte välja modell här',
  pickerError: 'Modellerna kunde inte läsas in',
  pickerBlocked: 'Modellvalet är pausat',
  pickerUnavailable: 'Inte tillgänglig',
  pickerCurrent: 'Vald',
  pickerNextTurn: 'Gäller från ditt nästa meddelande.',
  pickerFootnote:
    'Kommer ihåg ditt val till nästa bygge. Din administratör väljer vilka modeller företaget kan använda.',
  kindLabel: 'Artefakt',
  kindMenuLabel: 'Typ av artefakt',
  kindSheetTitle: 'Typ',
  kindUnavailable: 'Inte tillgänglig ännu',
  kindGroupApp: 'Interaktiv',
  kindGroupRead: 'Gjord för att läsas eller skickas',
  checkedTitle: 'Kontrollerat innan jag sa klart',
  checkedPassed: 'Godkänt',
  checkedFailed: 'Inte godkänt',
  checkedBlocked: 'Kunde inte köras',
  checkedStale: 'Inaktuell: artefakten ändrades efter kontrollen',
  checkNow: 'Kontrollera',
  checkAgain: 'Kontrollera igen',
  checkedAbsent: 'Inte kontrollerad ännu',
  checkedScreens: '{count} skärmar fångade',
  checkedLimitations: 'Det här täckte kontrollen inte',
  checkedVersion: 'Version {version}',
  skillsLabel: 'Kunskaper',
  skillsEmpty: 'Inga kunskaper ännu',
  skillsLoading: 'Läser in kunskaper',
  skillsError: 'Kunskaperna kunde inte läsas in',
  skillsDenied: 'Du kan inte använda kunskaper här',
  skillsAttached: 'Använder den här kunskapen',
  imageAttach: 'Bifoga en bild',
  imageDrop: 'Släpp en bild för att börja från den',
  imageNotImage: 'Filen är inte en bild.',
  imageStatus: 'Bild {label}: {status}',
};

type Key = keyof typeof en;

export type ArtifactComposerCopy = Readonly<Record<Key, string>>;

const translated: Omit<NamespaceCatalog<Key>, 'en' | 'sv'> = {
  // Bulgarian
  bg: {
    pickerLabel: 'Създаване с',
    pickerLoading: 'Зареждане на моделите',
    pickerNone: 'Няма наличен модел',
    pickerDenied: 'Тук не можете да избирате модел',
    pickerError: 'Моделите не можаха да бъдат заредени',
    pickerBlocked: 'Изборът на модел е на пауза',
    pickerUnavailable: 'Не е налично',
    pickerCurrent: 'Избрано',
    pickerNextTurn: 'Важи от следващото ви съобщение.',
    pickerFootnote:
      'Запомня се за следващите ви изграждания. Вашият администратор определя кои модели може да ползва фирмата ви.',
    kindLabel: 'Артефакт',
    kindMenuLabel: 'Вид артефакт',
    kindSheetTitle: 'Вид',
    kindUnavailable: 'Още не е налично',
    kindGroupApp: 'Интерактивен',
    kindGroupRead: 'Направен, за да се чете или изпраща',
    checkedTitle: 'Проверено, преди да кажа „готово“',
    checkedPassed: 'Успешно',
    checkedFailed: 'Неуспешно',
    checkedBlocked: 'Не можа да се изпълни',
    checkedStale: 'Остаряло: артефактът се промени след тази проверка',
    checkNow: 'Проверка',
    checkAgain: 'Проверка отново',
    checkedAbsent: 'Още не е проверено',
    checkedScreens: 'Заснети екрани: {count}',
    checkedLimitations: 'Какво тази проверка не покри',
    checkedVersion: 'Версия {version}',
    skillsLabel: 'Скилове',
    skillsEmpty: 'Още няма скилове',
    skillsLoading: 'Зареждане на скиловете',
    skillsError: 'Скиловете не можаха да бъдат заредени',
    skillsDenied: 'Тук не можете да ползвате скилове',
    skillsAttached: 'Използва този скил',
    imageAttach: 'Прикачване на изображение',
    imageDrop: 'Пуснете изображение, за да започнете от него',
    imageNotImage: 'Този файл не е изображение.',
    imageStatus: 'Изображение {label}: {status}',
  },
  // Czech
  cs: {
    pickerLabel: 'Vytvářet pomocí',
    pickerLoading: 'Načítají se modely',
    pickerNone: 'Není k dispozici žádný model',
    pickerDenied: 'Zde nemůžete vybrat model',
    pickerError: 'Modely se nepodařilo načíst',
    pickerBlocked: 'Výběr modelu je pozastaven',
    pickerUnavailable: 'Není k dispozici',
    pickerCurrent: 'Vybráno',
    pickerNextTurn: 'Platí od vaší další zprávy.',
    pickerFootnote:
      'Zapamatováno pro vaše další sestavení. Správce rozhoduje, které modely může vaše firma používat.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Druh artefaktu',
    kindSheetTitle: 'Druh',
    kindUnavailable: 'Zatím není k dispozici',
    kindGroupApp: 'Interaktivní',
    kindGroupRead: 'Vytvořeno ke čtení nebo odeslání',
    checkedTitle: 'Zkontrolováno, než jsem řekl „hotovo“',
    checkedPassed: 'Prošlo',
    checkedFailed: 'Neprošlo',
    checkedBlocked: 'Nepodařilo se spustit',
    checkedStale: 'Zastaralé: artefakt se po této kontrole změnil',
    checkNow: 'Zkontrolovat',
    checkAgain: 'Zkontrolovat znovu',
    checkedAbsent: 'Zatím nezkontrolováno',
    checkedScreens: 'Zachycené obrazovky: {count}',
    checkedLimitations: 'Co tato kontrola nepokryla',
    checkedVersion: 'Verze {version}',
    skillsLabel: 'Skilly',
    skillsEmpty: 'Zatím žádné skilly',
    skillsLoading: 'Načítají se skilly',
    skillsError: 'Skilly se nepodařilo načíst',
    skillsDenied: 'Zde nemůžete používat skilly',
    skillsAttached: 'Používá tento skill',
    imageAttach: 'Připojit obrázek',
    imageDrop: 'Přetáhněte obrázek a začněte od něj',
    imageNotImage: 'Tento soubor není obrázek.',
    imageStatus: 'Obrázek {label}: {status}',
  },
  // Danish
  da: {
    pickerLabel: 'Byg med',
    pickerLoading: 'Indlæser modeller',
    pickerNone: 'Ingen model tilgængelig',
    pickerDenied: 'Du kan ikke vælge model her',
    pickerError: 'Modellerne kunne ikke indlæses',
    pickerBlocked: 'Valg af model er sat på pause',
    pickerUnavailable: 'Ikke tilgængelig',
    pickerCurrent: 'Valgt',
    pickerNextTurn: 'Gælder fra din næste besked.',
    pickerFootnote:
      'Huskes til dine næste builds. Din administrator vælger, hvilke modeller din virksomhed kan bruge.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Slags artefakt',
    kindSheetTitle: 'Slags',
    kindUnavailable: 'Endnu ikke tilgængelig',
    kindGroupApp: 'Interaktiv',
    kindGroupRead: 'Lavet til at blive læst eller sendt',
    checkedTitle: 'Tjekket, før jeg sagde „færdig”',
    checkedPassed: 'Bestået',
    checkedFailed: 'Ikke bestået',
    checkedBlocked: 'Kunne ikke køre',
    checkedStale: 'Forældet: artefaktet ændrede sig efter dette tjek',
    checkNow: 'Tjek',
    checkAgain: 'Tjek igen',
    checkedAbsent: 'Endnu ikke tjekket',
    checkedScreens: 'Optagede skærme: {count}',
    checkedLimitations: 'Hvad dette tjek ikke dækkede',
    checkedVersion: 'Version {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Ingen skills endnu',
    skillsLoading: 'Indlæser skills',
    skillsError: 'Skills kunne ikke indlæses',
    skillsDenied: 'Du kan ikke bruge skills her',
    skillsAttached: 'Bruger denne skill',
    imageAttach: 'Vedhæft et billede',
    imageDrop: 'Slip et billede for at starte ud fra det',
    imageNotImage: 'Den fil er ikke et billede.',
    imageStatus: 'Billede {label}: {status}',
  },
  // German
  de: {
    pickerLabel: 'Erstellen mit',
    pickerLoading: 'Modelle werden geladen',
    pickerNone: 'Kein Modell verfügbar',
    pickerDenied: 'Sie können hier kein Modell auswählen',
    pickerError: 'Modelle konnten nicht geladen werden',
    pickerBlocked: 'Die Modellauswahl ist pausiert',
    pickerUnavailable: 'Nicht verfügbar',
    pickerCurrent: 'Ausgewählt',
    pickerNextTurn: 'Gilt ab Ihrer nächsten Nachricht.',
    pickerFootnote:
      'Wird für Ihre nächsten Builds gespeichert. Ihre Administration legt fest, welche Modelle Ihr Unternehmen verwenden kann.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Art des Artefakts',
    kindSheetTitle: 'Art',
    kindUnavailable: 'Noch nicht verfügbar',
    kindGroupApp: 'Interaktiv',
    kindGroupRead: 'Zum Lesen oder Versenden gemacht',
    checkedTitle: 'Geprüft, bevor ich „fertig“ sagte',
    checkedPassed: 'Bestanden',
    checkedFailed: 'Nicht bestanden',
    checkedBlocked: 'Konnte nicht ausgeführt werden',
    checkedStale: 'Veraltet: Das Artefakt hat sich nach dieser Prüfung geändert',
    checkNow: 'Prüfen',
    checkAgain: 'Erneut prüfen',
    checkedAbsent: 'Noch nicht geprüft',
    checkedScreens: 'Erfasste Bildschirme: {count}',
    checkedLimitations: 'Das hat diese Prüfung nicht abgedeckt',
    checkedVersion: 'Version {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Noch keine Skills',
    skillsLoading: 'Skills werden geladen',
    skillsError: 'Skills konnten nicht geladen werden',
    skillsDenied: 'Sie können hier keine Skills verwenden',
    skillsAttached: 'Verwendet diesen Skill',
    imageAttach: 'Bild anhängen',
    imageDrop: 'Bild hierher ziehen, um damit zu starten',
    imageNotImage: 'Diese Datei ist kein Bild.',
    imageStatus: 'Bild {label}: {status}',
  },
  // Greek
  el: {
    pickerLabel: 'Δημιουργία με',
    pickerLoading: 'Φόρτωση μοντέλων',
    pickerNone: 'Κανένα διαθέσιμο μοντέλο',
    pickerDenied: 'Δεν μπορείτε να επιλέξετε μοντέλο εδώ',
    pickerError: 'Τα μοντέλα δεν μπόρεσαν να φορτωθούν',
    pickerBlocked: 'Η επιλογή μοντέλου έχει διακοπεί προσωρινά',
    pickerUnavailable: 'Μη διαθέσιμο',
    pickerCurrent: 'Επιλεγμένο',
    pickerNextTurn: 'Ισχύει από το επόμενο μήνυμά σας.',
    pickerFootnote:
      'Θα το θυμόμαστε για τις επόμενες δημιουργίες σας. Ο διαχειριστής σας ορίζει ποια μοντέλα μπορεί να χρησιμοποιεί η εταιρεία σας.',
    kindLabel: 'Αντικείμενο',
    kindMenuLabel: 'Είδος αντικειμένου',
    kindSheetTitle: 'Είδος',
    kindUnavailable: 'Δεν είναι ακόμη διαθέσιμο',
    kindGroupApp: 'Διαδραστικό',
    kindGroupRead: 'Φτιαγμένο για ανάγνωση ή αποστολή',
    checkedTitle: 'Ελέγχθηκε πριν πω «έτοιμο»',
    checkedPassed: 'Πέρασε',
    checkedFailed: 'Δεν πέρασε',
    checkedBlocked: 'Δεν μπόρεσε να εκτελεστεί',
    checkedStale: 'Παλιό: το αντικείμενο άλλαξε μετά από αυτόν τον έλεγχο',
    checkNow: 'Έλεγχος',
    checkAgain: 'Έλεγχος ξανά',
    checkedAbsent: 'Δεν έχει ελεγχθεί ακόμη',
    checkedScreens: 'Οθόνες που καταγράφηκαν: {count}',
    checkedLimitations: 'Τι δεν κάλυψε αυτός ο έλεγχος',
    checkedVersion: 'Έκδοση {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Δεν υπάρχουν ακόμη skills',
    skillsLoading: 'Φόρτωση skills',
    skillsError: 'Τα skills δεν μπόρεσαν να φορτωθούν',
    skillsDenied: 'Δεν μπορείτε να χρησιμοποιήσετε skills εδώ',
    skillsAttached: 'Χρησιμοποιεί αυτό το skill',
    imageAttach: 'Επισύναψη εικόνας',
    imageDrop: 'Αφήστε μια εικόνα για να ξεκινήσετε από αυτήν',
    imageNotImage: 'Αυτό το αρχείο δεν είναι εικόνα.',
    imageStatus: 'Εικόνα {label}: {status}',
  },
  // Spanish
  es: {
    pickerLabel: 'Crear con',
    pickerLoading: 'Cargando modelos',
    pickerNone: 'No hay ningún modelo disponible',
    pickerDenied: 'Aquí no puedes elegir un modelo',
    pickerError: 'No se pudieron cargar los modelos',
    pickerBlocked: 'La elección de modelo está en pausa',
    pickerUnavailable: 'No disponible',
    pickerCurrent: 'Seleccionado',
    pickerNextTurn: 'Se aplica desde tu próximo mensaje.',
    pickerFootnote:
      'Se recordará para tus próximas creaciones. Tu administrador decide qué modelos puede usar tu empresa.',
    kindLabel: 'Artefacto',
    kindMenuLabel: 'Tipo de artefacto',
    kindSheetTitle: 'Tipo',
    kindUnavailable: 'Todavía no disponible',
    kindGroupApp: 'Interactivo',
    kindGroupRead: 'Hecho para leerse o enviarse',
    checkedTitle: 'Comprobado antes de decir «listo»',
    checkedPassed: 'Superada',
    checkedFailed: 'No superada',
    checkedBlocked: 'No se pudo ejecutar',
    checkedStale: 'Obsoleta: el artefacto cambió después de esta comprobación',
    checkNow: 'Comprobar',
    checkAgain: 'Comprobar de nuevo',
    checkedAbsent: 'Aún sin comprobar',
    checkedScreens: 'Pantallas capturadas: {count}',
    checkedLimitations: 'Lo que esta comprobación no cubrió',
    checkedVersion: 'Versión {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Aún no hay skills',
    skillsLoading: 'Cargando skills',
    skillsError: 'No se pudieron cargar los skills',
    skillsDenied: 'Aquí no puedes usar skills',
    skillsAttached: 'Usa este skill',
    imageAttach: 'Adjuntar una imagen',
    imageDrop: 'Suelta una imagen para empezar con ella',
    imageNotImage: 'Ese archivo no es una imagen.',
    imageStatus: 'Imagen {label}: {status}',
  },
  // Estonian
  et: {
    pickerLabel: 'Loo mudeliga',
    pickerLoading: 'Mudelite laadimine',
    pickerNone: 'Ühtegi mudelit pole saadaval',
    pickerDenied: 'Siin ei saa mudelit valida',
    pickerError: 'Mudeleid ei õnnestunud laadida',
    pickerBlocked: 'Mudeli valik on peatatud',
    pickerUnavailable: 'Pole saadaval',
    pickerCurrent: 'Valitud',
    pickerNextTurn: 'Kehtib sinu järgmisest sõnumist.',
    pickerFootnote:
      'Jäetakse meelde sinu järgmisteks koostamisteks. Sinu administraator otsustab, milliseid mudeleid sinu ettevõte kasutada võib.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Artefakti liik',
    kindSheetTitle: 'Liik',
    kindUnavailable: 'Veel pole saadaval',
    kindGroupApp: 'Interaktiivne',
    kindGroupRead: 'Tehtud lugemiseks või saatmiseks',
    checkedTitle: 'Kontrollitud enne, kui ütlesin „valmis”',
    checkedPassed: 'Läbitud',
    checkedFailed: 'Ei läbitud',
    checkedBlocked: 'Käivitamine ei õnnestunud',
    checkedStale: 'Aegunud: artefakt muutus pärast seda kontrolli',
    checkNow: 'Kontrolli',
    checkAgain: 'Kontrolli uuesti',
    checkedAbsent: 'Veel kontrollimata',
    checkedScreens: 'Jäädvustatud ekraane: {count}',
    checkedLimitations: 'Mida see kontroll ei hõlmanud',
    checkedVersion: 'Versioon {version}',
    skillsLabel: 'Skillid',
    skillsEmpty: 'Skille veel pole',
    skillsLoading: 'Skillide laadimine',
    skillsError: 'Skille ei õnnestunud laadida',
    skillsDenied: 'Siin ei saa skille kasutada',
    skillsAttached: 'Kasutab seda skilli',
    imageAttach: 'Lisa pilt',
    imageDrop: 'Lohista pilt siia, et sellest alustada',
    imageNotImage: 'See fail pole pilt.',
    imageStatus: 'Pilt {label}: {status}',
  },
  // Finnish
  fi: {
    pickerLabel: 'Rakenna mallilla',
    pickerLoading: 'Ladataan malleja',
    pickerNone: 'Mallia ei ole saatavilla',
    pickerDenied: 'Et voi valita mallia täällä',
    pickerError: 'Malleja ei voitu ladata',
    pickerBlocked: 'Mallin valinta on tauolla',
    pickerUnavailable: 'Ei saatavilla',
    pickerCurrent: 'Valittu',
    pickerNextTurn: 'Tulee voimaan seuraavasta viestistäsi.',
    pickerFootnote:
      'Muistetaan seuraavia rakennuksiasi varten. Ylläpitäjäsi valitsee, mitä malleja yrityksesi voi käyttää.',
    kindLabel: 'Artefakti',
    kindMenuLabel: 'Artefaktin laji',
    kindSheetTitle: 'Laji',
    kindUnavailable: 'Ei vielä saatavilla',
    kindGroupApp: 'Interaktiivinen',
    kindGroupRead: 'Tehty luettavaksi tai lähetettäväksi',
    checkedTitle: 'Tarkistettu ennen kuin sanoin ”valmis”',
    checkedPassed: 'Hyväksytty',
    checkedFailed: 'Ei hyväksytty',
    checkedBlocked: 'Ei voitu suorittaa',
    checkedStale: 'Vanhentunut: artefakti muuttui tämän tarkistuksen jälkeen',
    checkNow: 'Tarkista',
    checkAgain: 'Tarkista uudelleen',
    checkedAbsent: 'Ei vielä tarkistettu',
    checkedScreens: 'Tallennettuja näyttöjä: {count}',
    checkedLimitations: 'Mitä tämä tarkistus ei kattanut',
    checkedVersion: 'Versio {version}',
    skillsLabel: 'Skillit',
    skillsEmpty: 'Ei vielä skillejä',
    skillsLoading: 'Ladataan skillejä',
    skillsError: 'Skillejä ei voitu ladata',
    skillsDenied: 'Et voi käyttää skillejä täällä',
    skillsAttached: 'Käyttää tätä skilliä',
    imageAttach: 'Liitä kuva',
    imageDrop: 'Pudota kuva aloittaaksesi siitä',
    imageNotImage: 'Tämä tiedosto ei ole kuva.',
    imageStatus: 'Kuva {label}: {status}',
  },
  // French
  fr: {
    pickerLabel: 'Construire avec',
    pickerLoading: 'Chargement des modèles',
    pickerNone: 'Aucun modèle disponible',
    pickerDenied: 'Vous ne pouvez pas choisir de modèle ici',
    pickerError: 'Les modèles n’ont pas pu être chargés',
    pickerBlocked: 'Le choix du modèle est en pause',
    pickerUnavailable: 'Non disponible',
    pickerCurrent: 'Sélectionné',
    pickerNextTurn: 'S’applique à partir de votre prochain message.',
    pickerFootnote:
      'Mémorisé pour vos prochaines créations. Votre administrateur choisit les modèles que votre entreprise peut utiliser.',
    kindLabel: 'Artefact',
    kindMenuLabel: 'Type d’artefact',
    kindSheetTitle: 'Type',
    kindUnavailable: 'Pas encore disponible',
    kindGroupApp: 'Interactif',
    kindGroupRead: 'Fait pour être lu ou envoyé',
    checkedTitle: 'Vérifié avant que je dise « terminé »',
    checkedPassed: 'Réussi',
    checkedFailed: 'Échec',
    checkedBlocked: 'N’a pas pu s’exécuter',
    checkedStale: 'Obsolète : l’artefact a changé après cette vérification',
    checkNow: 'Vérifier',
    checkAgain: 'Vérifier à nouveau',
    checkedAbsent: 'Pas encore vérifié',
    checkedScreens: 'Écrans capturés : {count}',
    checkedLimitations: 'Ce que cette vérification n’a pas couvert',
    checkedVersion: 'Version {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Aucun skill pour le moment',
    skillsLoading: 'Chargement des skills',
    skillsError: 'Les skills n’ont pas pu être chargés',
    skillsDenied: 'Vous ne pouvez pas utiliser de skills ici',
    skillsAttached: 'Utilise ce skill',
    imageAttach: 'Joindre une image',
    imageDrop: 'Déposez une image pour partir de celle-ci',
    imageNotImage: 'Ce fichier n’est pas une image.',
    imageStatus: 'Image {label} : {status}',
  },
  // Irish
  ga: {
    pickerLabel: 'Tóg le',
    pickerLoading: 'Samhlacha á luchtú',
    pickerNone: 'Níl aon samhail ar fáil',
    pickerDenied: 'Ní féidir leat samhail a roghnú anseo',
    pickerError: 'Níorbh fhéidir na samhlacha a luchtú',
    pickerBlocked: 'Tá roghnú samhla ar sos',
    pickerUnavailable: 'Níl ar fáil',
    pickerCurrent: 'Roghnaithe',
    pickerNextTurn: 'Baineann sé ón gcéad teachtaireacht eile agat.',
    pickerFootnote:
      'Cuimhneofar air do do chéad tógálacha eile. Roghnaíonn do riarthóir na samhlacha is féidir le do chuideachta a úsáid.',
    kindLabel: 'Déantán',
    kindMenuLabel: 'Cineál déantáin',
    kindSheetTitle: 'Cineál',
    kindUnavailable: 'Níl ar fáil go fóill',
    kindGroupApp: 'Idirghníomhach',
    kindGroupRead: 'Déanta le léamh nó le seoladh',
    checkedTitle: 'Seiceáilte sular dhúirt mé “críochnaithe”',
    checkedPassed: 'Pas',
    checkedFailed: 'Theip',
    checkedBlocked: 'Níorbh fhéidir é a rith',
    checkedStale: 'As dáta: d’athraigh an déantán tar éis an tseiceála seo',
    checkNow: 'Seiceáil',
    checkAgain: 'Seiceáil arís',
    checkedAbsent: 'Gan seiceáil go fóill',
    checkedScreens: 'Scáileáin gafa: {count}',
    checkedLimitations: 'An méid nár chlúdaigh an tseiceáil seo',
    checkedVersion: 'Leagan {version}',
    skillsLabel: 'Scileanna',
    skillsEmpty: 'Níl aon scileanna ann go fóill',
    skillsLoading: 'Scileanna á luchtú',
    skillsError: 'Níorbh fhéidir na scileanna a luchtú',
    skillsDenied: 'Ní féidir leat scileanna a úsáid anseo',
    skillsAttached: 'Ag úsáid na scile seo',
    imageAttach: 'Ceangail íomhá',
    imageDrop: 'Scaoil íomhá chun tosú uaithi',
    imageNotImage: 'Ní íomhá an comhad sin.',
    imageStatus: 'Íomhá {label}: {status}',
  },
  // Croatian
  hr: {
    pickerLabel: 'Izradi pomoću',
    pickerLoading: 'Učitavanje modela',
    pickerNone: 'Nijedan model nije dostupan',
    pickerDenied: 'Ovdje ne možete odabrati model',
    pickerError: 'Modele nije bilo moguće učitati',
    pickerBlocked: 'Odabir modela je pauziran',
    pickerUnavailable: 'Nije dostupno',
    pickerCurrent: 'Odabrano',
    pickerNextTurn: 'Vrijedi od vaše sljedeće poruke.',
    pickerFootnote:
      'Zapamćeno za vaše sljedeće izrade. Administrator određuje koje modele vaša tvrtka može koristiti.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Vrsta artefakta',
    kindSheetTitle: 'Vrsta',
    kindUnavailable: 'Još nije dostupno',
    kindGroupApp: 'Interaktivno',
    kindGroupRead: 'Napravljeno za čitanje ili slanje',
    checkedTitle: 'Provjereno prije nego što sam rekao „gotovo”',
    checkedPassed: 'Prošlo',
    checkedFailed: 'Nije prošlo',
    checkedBlocked: 'Nije bilo moguće pokrenuti',
    checkedStale: 'Zastarjelo: artefakt se promijenio nakon ove provjere',
    checkNow: 'Provjeri',
    checkAgain: 'Provjeri ponovno',
    checkedAbsent: 'Još nije provjereno',
    checkedScreens: 'Snimljeni zasloni: {count}',
    checkedLimitations: 'Što ova provjera nije obuhvatila',
    checkedVersion: 'Verzija {version}',
    skillsLabel: 'Skillovi',
    skillsEmpty: 'Još nema skillova',
    skillsLoading: 'Učitavanje skillova',
    skillsError: 'Skillove nije bilo moguće učitati',
    skillsDenied: 'Ovdje ne možete koristiti skillove',
    skillsAttached: 'Koristi ovaj skill',
    imageAttach: 'Priloži sliku',
    imageDrop: 'Ispustite sliku da biste počeli od nje',
    imageNotImage: 'Ta datoteka nije slika.',
    imageStatus: 'Slika {label}: {status}',
  },
  // Hungarian
  hu: {
    pickerLabel: 'Készítés ezzel:',
    pickerLoading: 'Modellek betöltése',
    pickerNone: 'Nincs elérhető modell',
    pickerDenied: 'Itt nem választhatsz modellt',
    pickerError: 'A modelleket nem sikerült betölteni',
    pickerBlocked: 'A modellválasztás szünetel',
    pickerUnavailable: 'Nem érhető el',
    pickerCurrent: 'Kiválasztva',
    pickerNextTurn: 'A következő üzenetedtől érvényes.',
    pickerFootnote:
      'Megjegyezzük a következő építéseidhez. A rendszergazdád dönti el, mely modelleket használhatja a céged.',
    kindLabel: 'Artefaktum',
    kindMenuLabel: 'Az artefaktum fajtája',
    kindSheetTitle: 'Fajta',
    kindUnavailable: 'Még nem érhető el',
    kindGroupApp: 'Interaktív',
    kindGroupRead: 'Olvasásra vagy küldésre készült',
    checkedTitle: 'Ellenőrizve, mielőtt azt mondtam: „kész”',
    checkedPassed: 'Sikeres',
    checkedFailed: 'Sikertelen',
    checkedBlocked: 'Nem sikerült lefuttatni',
    checkedStale: 'Elavult: az artefaktum az ellenőrzés után megváltozott',
    checkNow: 'Ellenőrzés',
    checkAgain: 'Ellenőrzés újra',
    checkedAbsent: 'Még nincs ellenőrizve',
    checkedScreens: 'Rögzített képernyők: {count}',
    checkedLimitations: 'Amit ez az ellenőrzés nem fedett le',
    checkedVersion: '{version}. verzió',
    skillsLabel: 'Skillek',
    skillsEmpty: 'Még nincsenek skillek',
    skillsLoading: 'Skillek betöltése',
    skillsError: 'A skilleket nem sikerült betölteni',
    skillsDenied: 'Itt nem használhatsz skilleket',
    skillsAttached: 'Ezt a skillt használja',
    imageAttach: 'Kép csatolása',
    imageDrop: 'Húzz ide egy képet, hogy abból indulj ki',
    imageNotImage: 'Ez a fájl nem kép.',
    imageStatus: '{label} kép: {status}',
  },
  // Italian
  it: {
    pickerLabel: 'Crea con',
    pickerLoading: 'Caricamento dei modelli',
    pickerNone: 'Nessun modello disponibile',
    pickerDenied: 'Qui non puoi scegliere un modello',
    pickerError: 'Impossibile caricare i modelli',
    pickerBlocked: 'La scelta del modello è in pausa',
    pickerUnavailable: 'Non disponibile',
    pickerCurrent: 'Selezionato',
    pickerNextTurn: 'Vale dal tuo prossimo messaggio.',
    pickerFootnote:
      'Ricordato per le tue prossime creazioni. Il tuo amministratore decide quali modelli può usare la tua azienda.',
    kindLabel: 'Artefatto',
    kindMenuLabel: 'Tipo di artefatto',
    kindSheetTitle: 'Tipo',
    kindUnavailable: 'Non ancora disponibile',
    kindGroupApp: 'Interattivo',
    kindGroupRead: 'Fatto per essere letto o inviato',
    checkedTitle: 'Controllato prima di dire «fatto»',
    checkedPassed: 'Superato',
    checkedFailed: 'Non superato',
    checkedBlocked: 'Impossibile eseguire',
    checkedStale: 'Obsoleto: l’artefatto è cambiato dopo questo controllo',
    checkNow: 'Controlla',
    checkAgain: 'Controlla di nuovo',
    checkedAbsent: 'Non ancora controllato',
    checkedScreens: 'Schermate acquisite: {count}',
    checkedLimitations: 'Cosa questo controllo non ha coperto',
    checkedVersion: 'Versione {version}',
    skillsLabel: 'Skill',
    skillsEmpty: 'Ancora nessuna skill',
    skillsLoading: 'Caricamento delle skill',
    skillsError: 'Impossibile caricare le skill',
    skillsDenied: 'Qui non puoi usare le skill',
    skillsAttached: 'Usa questa skill',
    imageAttach: 'Allega un’immagine',
    imageDrop: 'Trascina un’immagine per partire da essa',
    imageNotImage: 'Quel file non è un’immagine.',
    imageStatus: 'Immagine {label}: {status}',
  },
  // Lithuanian
  lt: {
    pickerLabel: 'Kurti naudojant',
    pickerLoading: 'Įkeliami modeliai',
    pickerNone: 'Nėra pasiekiamo modelio',
    pickerDenied: 'Čia negalite pasirinkti modelio',
    pickerError: 'Modelių nepavyko įkelti',
    pickerBlocked: 'Modelio pasirinkimas pristabdytas',
    pickerUnavailable: 'Nepasiekiama',
    pickerCurrent: 'Pasirinkta',
    pickerNextTurn: 'Galioja nuo jūsų kito pranešimo.',
    pickerFootnote:
      'Bus prisiminta jūsų kitiems kūrimams. Administratorius nusprendžia, kuriuos modelius gali naudoti jūsų įmonė.',
    kindLabel: 'Artefaktas',
    kindMenuLabel: 'Artefakto rūšis',
    kindSheetTitle: 'Rūšis',
    kindUnavailable: 'Dar nepasiekiama',
    kindGroupApp: 'Interaktyvus',
    kindGroupRead: 'Sukurta skaityti arba siųsti',
    checkedTitle: 'Patikrinta, kol nepasakiau „baigta“',
    checkedPassed: 'Išlaikyta',
    checkedFailed: 'Neišlaikyta',
    checkedBlocked: 'Nepavyko paleisti',
    checkedStale: 'Pasenę: artefaktas pasikeitė po šio patikrinimo',
    checkNow: 'Tikrinti',
    checkAgain: 'Tikrinti dar kartą',
    checkedAbsent: 'Dar nepatikrinta',
    checkedScreens: 'Užfiksuoti ekranai: {count}',
    checkedLimitations: 'Ko šis patikrinimas neapėmė',
    checkedVersion: '{version} versija',
    skillsLabel: 'Skilai',
    skillsEmpty: 'Skilų dar nėra',
    skillsLoading: 'Įkeliami skilai',
    skillsError: 'Skilų nepavyko įkelti',
    skillsDenied: 'Čia negalite naudoti skilų',
    skillsAttached: 'Naudoja šį skilą',
    imageAttach: 'Pridėti vaizdą',
    imageDrop: 'Nuleiskite vaizdą, kad pradėtumėte nuo jo',
    imageNotImage: 'Šis failas nėra vaizdas.',
    imageStatus: 'Vaizdas {label}: {status}',
  },
  // Latvian
  lv: {
    pickerLabel: 'Veidot ar',
    pickerLoading: 'Notiek modeļu ielāde',
    pickerNone: 'Nav pieejama neviena modeļa',
    pickerDenied: 'Šeit nevarat izvēlēties modeli',
    pickerError: 'Modeļus neizdevās ielādēt',
    pickerBlocked: 'Modeļa izvēle ir apturēta',
    pickerUnavailable: 'Nav pieejams',
    pickerCurrent: 'Atlasīts',
    pickerNextTurn: 'Spēkā no jūsu nākamā ziņojuma.',
    pickerFootnote:
      'Tiks atcerēts jūsu nākamajiem veidojumiem. Administrators izlemj, kurus modeļus var izmantot jūsu uzņēmums.',
    kindLabel: 'Artefakts',
    kindMenuLabel: 'Artefakta veids',
    kindSheetTitle: 'Veids',
    kindUnavailable: 'Vēl nav pieejams',
    kindGroupApp: 'Interaktīvs',
    kindGroupRead: 'Izveidots lasīšanai vai sūtīšanai',
    checkedTitle: 'Pārbaudīts, pirms es teicu „gatavs”',
    checkedPassed: 'Izturēta',
    checkedFailed: 'Neizturēta',
    checkedBlocked: 'Neizdevās palaist',
    checkedStale: 'Novecojis: artefakts mainījās pēc šīs pārbaudes',
    checkNow: 'Pārbaudīt',
    checkAgain: 'Pārbaudīt vēlreiz',
    checkedAbsent: 'Vēl nav pārbaudīts',
    checkedScreens: 'Uzņemtie ekrāni: {count}',
    checkedLimitations: 'Ko šī pārbaude neaptvēra',
    checkedVersion: 'Versija {version}',
    skillsLabel: 'Skili',
    skillsEmpty: 'Vēl nav skilu',
    skillsLoading: 'Notiek skilu ielāde',
    skillsError: 'Skilus neizdevās ielādēt',
    skillsDenied: 'Šeit nevarat izmantot skilus',
    skillsAttached: 'Izmanto šo skilu',
    imageAttach: 'Pievienot attēlu',
    imageDrop: 'Nometiet attēlu, lai sāktu no tā',
    imageNotImage: 'Šis fails nav attēls.',
    imageStatus: 'Attēls {label}: {status}',
  },
  // Maltese
  mt: {
    pickerLabel: 'Ibni b’',
    pickerLoading: 'Qed jitgħabbew il-mudelli',
    pickerNone: 'L-ebda mudell disponibbli',
    pickerDenied: 'Ma tistax tagħżel mudell hawnhekk',
    pickerError: 'Il-mudelli ma setgħux jitgħabbew',
    pickerBlocked: 'L-għażla tal-mudell hija sospiża',
    pickerUnavailable: 'Mhux disponibbli',
    pickerCurrent: 'Magħżul',
    pickerNextTurn: 'Tapplika mill-messaġġ li jmiss tiegħek.',
    pickerFootnote:
      'Se niftakruha għall-bini li jmiss tiegħek. L-amministratur tiegħek jagħżel liema mudelli tista’ tuża l-kumpanija tiegħek.',
    kindLabel: 'Artifatt',
    kindMenuLabel: 'Tip ta’ artifatt',
    kindSheetTitle: 'Tip',
    kindUnavailable: 'Għadu mhux disponibbli',
    kindGroupApp: 'Interattiv',
    kindGroupRead: 'Magħmul biex jinqara jew jintbagħat',
    checkedTitle: 'Iċċekkjat qabel għidt “lest”',
    checkedPassed: 'Għadda',
    checkedFailed: 'Ma għaddiex',
    checkedBlocked: 'Ma setax jaħdem',
    checkedStale: 'Skadut: l-artifatt inbidel wara dan iċ-ċekk',
    checkNow: 'Iċċekkja',
    checkAgain: 'Iċċekkja mill-ġdid',
    checkedAbsent: 'Għadu mhux iċċekkjat',
    checkedScreens: 'Skrins maqbuda: {count}',
    checkedLimitations: 'Dak li dan iċ-ċekk ma kopriex',
    checkedVersion: 'Verżjoni {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Għad m’hemm l-ebda skills',
    skillsLoading: 'Qed jitgħabbew l-skills',
    skillsError: 'L-skills ma setgħux jitgħabbew',
    skillsDenied: 'Ma tistax tuża skills hawnhekk',
    skillsAttached: 'Qed juża dan l-skill',
    imageAttach: 'Waħħal immaġni',
    imageDrop: 'Waqqa’ immaġni biex tibda minnha',
    imageNotImage: 'Dak il-fajl mhuwiex immaġni.',
    imageStatus: 'Immaġni {label}: {status}',
  },
  // Dutch
  nl: {
    pickerLabel: 'Bouwen met',
    pickerLoading: 'Modellen laden',
    pickerNone: 'Geen model beschikbaar',
    pickerDenied: 'Je kunt hier geen model kiezen',
    pickerError: 'Modellen konden niet worden geladen',
    pickerBlocked: 'Modelkeuze is gepauzeerd',
    pickerUnavailable: 'Niet beschikbaar',
    pickerCurrent: 'Geselecteerd',
    pickerNextTurn: 'Geldt vanaf je volgende bericht.',
    pickerFootnote:
      'Onthouden voor je volgende builds. Je beheerder bepaalt welke modellen je bedrijf mag gebruiken.',
    kindLabel: 'Artefact',
    kindMenuLabel: 'Soort artefact',
    kindSheetTitle: 'Soort',
    kindUnavailable: 'Nog niet beschikbaar',
    kindGroupApp: 'Interactief',
    kindGroupRead: 'Gemaakt om te lezen of te versturen',
    checkedTitle: 'Gecontroleerd voordat ik „klaar” zei',
    checkedPassed: 'Geslaagd',
    checkedFailed: 'Niet geslaagd',
    checkedBlocked: 'Kon niet worden uitgevoerd',
    checkedStale: 'Verouderd: het artefact is na deze controle gewijzigd',
    checkNow: 'Controleren',
    checkAgain: 'Opnieuw controleren',
    checkedAbsent: 'Nog niet gecontroleerd',
    checkedScreens: 'Vastgelegde schermen: {count}',
    checkedLimitations: 'Wat deze controle niet heeft gedekt',
    checkedVersion: 'Versie {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Nog geen skills',
    skillsLoading: 'Skills laden',
    skillsError: 'Skills konden niet worden geladen',
    skillsDenied: 'Je kunt hier geen skills gebruiken',
    skillsAttached: 'Gebruikt deze skill',
    imageAttach: 'Afbeelding toevoegen',
    imageDrop: 'Sleep een afbeelding om ermee te beginnen',
    imageNotImage: 'Dat bestand is geen afbeelding.',
    imageStatus: 'Afbeelding {label}: {status}',
  },
  // Polish
  pl: {
    pickerLabel: 'Twórz za pomocą',
    pickerLoading: 'Ładowanie modeli',
    pickerNone: 'Brak dostępnego modelu',
    pickerDenied: 'Nie możesz tutaj wybrać modelu',
    pickerError: 'Nie udało się załadować modeli',
    pickerBlocked: 'Wybór modelu jest wstrzymany',
    pickerUnavailable: 'Niedostępne',
    pickerCurrent: 'Wybrano',
    pickerNextTurn: 'Obowiązuje od twojej następnej wiadomości.',
    pickerFootnote:
      'Zapamiętane na kolejne kompilacje. Twój administrator decyduje, z jakich modeli może korzystać twoja firma.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Rodzaj artefaktu',
    kindSheetTitle: 'Rodzaj',
    kindUnavailable: 'Jeszcze niedostępne',
    kindGroupApp: 'Interaktywny',
    kindGroupRead: 'Stworzony do czytania lub wysyłania',
    checkedTitle: 'Sprawdzone, zanim powiedziałem „gotowe”',
    checkedPassed: 'Zaliczone',
    checkedFailed: 'Niezaliczone',
    checkedBlocked: 'Nie udało się uruchomić',
    checkedStale: 'Nieaktualne: artefakt zmienił się po tym sprawdzeniu',
    checkNow: 'Sprawdź',
    checkAgain: 'Sprawdź ponownie',
    checkedAbsent: 'Jeszcze nie sprawdzono',
    checkedScreens: 'Zarejestrowane ekrany: {count}',
    checkedLimitations: 'Czego to sprawdzenie nie objęło',
    checkedVersion: 'Wersja {version}',
    skillsLabel: 'Skille',
    skillsEmpty: 'Brak skilli',
    skillsLoading: 'Ładowanie skilli',
    skillsError: 'Nie udało się załadować skilli',
    skillsDenied: 'Nie możesz tutaj używać skilli',
    skillsAttached: 'Używa tego skilla',
    imageAttach: 'Dołącz obraz',
    imageDrop: 'Upuść obraz, aby od niego zacząć',
    imageNotImage: 'Ten plik nie jest obrazem.',
    imageStatus: 'Obraz {label}: {status}',
  },
  // Portuguese
  pt: {
    pickerLabel: 'Criar com',
    pickerLoading: 'A carregar modelos',
    pickerNone: 'Nenhum modelo disponível',
    pickerDenied: 'Não podes escolher um modelo aqui',
    pickerError: 'Não foi possível carregar os modelos',
    pickerBlocked: 'A escolha de modelo está em pausa',
    pickerUnavailable: 'Não disponível',
    pickerCurrent: 'Selecionado',
    pickerNextTurn: 'Aplica-se a partir da tua próxima mensagem.',
    pickerFootnote:
      'Fica guardado para as tuas próximas criações. O teu administrador escolhe os modelos que a tua empresa pode usar.',
    kindLabel: 'Artefacto',
    kindMenuLabel: 'Tipo de artefacto',
    kindSheetTitle: 'Tipo',
    kindUnavailable: 'Ainda não disponível',
    kindGroupApp: 'Interativo',
    kindGroupRead: 'Feito para ser lido ou enviado',
    checkedTitle: 'Verificado antes de eu dizer «concluído»',
    checkedPassed: 'Aprovado',
    checkedFailed: 'Não aprovado',
    checkedBlocked: 'Não foi possível executar',
    checkedStale: 'Desatualizado: o artefacto mudou depois desta verificação',
    checkNow: 'Verificar',
    checkAgain: 'Verificar novamente',
    checkedAbsent: 'Ainda não verificado',
    checkedScreens: 'Ecrãs capturados: {count}',
    checkedLimitations: 'O que esta verificação não cobriu',
    checkedVersion: 'Versão {version}',
    skillsLabel: 'Skills',
    skillsEmpty: 'Ainda sem skills',
    skillsLoading: 'A carregar skills',
    skillsError: 'Não foi possível carregar as skills',
    skillsDenied: 'Não podes usar skills aqui',
    skillsAttached: 'A usar esta skill',
    imageAttach: 'Anexar uma imagem',
    imageDrop: 'Larga uma imagem para começar com ela',
    imageNotImage: 'Esse ficheiro não é uma imagem.',
    imageStatus: 'Imagem {label}: {status}',
  },
  // Romanian
  ro: {
    pickerLabel: 'Construiește cu',
    pickerLoading: 'Se încarcă modelele',
    pickerNone: 'Niciun model disponibil',
    pickerDenied: 'Nu poți alege un model aici',
    pickerError: 'Modelele nu au putut fi încărcate',
    pickerBlocked: 'Alegerea modelului este suspendată',
    pickerUnavailable: 'Indisponibil',
    pickerCurrent: 'Selectat',
    pickerNextTurn: 'Se aplică de la următorul tău mesaj.',
    pickerFootnote:
      'Reținut pentru următoarele tale construiri. Administratorul tău decide ce modele poate folosi compania ta.',
    kindLabel: 'Artefact',
    kindMenuLabel: 'Tipul artefactului',
    kindSheetTitle: 'Tip',
    kindUnavailable: 'Încă indisponibil',
    kindGroupApp: 'Interactiv',
    kindGroupRead: 'Făcut pentru a fi citit sau trimis',
    checkedTitle: 'Verificat înainte să spun „gata”',
    checkedPassed: 'Reușit',
    checkedFailed: 'Nereușit',
    checkedBlocked: 'Nu a putut rula',
    checkedStale: 'Învechit: artefactul s-a schimbat după această verificare',
    checkNow: 'Verifică',
    checkAgain: 'Verifică din nou',
    checkedAbsent: 'Încă neverificat',
    checkedScreens: 'Ecrane capturate: {count}',
    checkedLimitations: 'Ce nu a acoperit această verificare',
    checkedVersion: 'Versiunea {version}',
    skillsLabel: 'Skilluri',
    skillsEmpty: 'Încă niciun skill',
    skillsLoading: 'Se încarcă skilurile',
    skillsError: 'Skilurile nu au putut fi încărcate',
    skillsDenied: 'Nu poți folosi skilluri aici',
    skillsAttached: 'Folosește acest skill',
    imageAttach: 'Atașează o imagine',
    imageDrop: 'Plasează o imagine ca să pornești de la ea',
    imageNotImage: 'Acest fișier nu este o imagine.',
    imageStatus: 'Imaginea {label}: {status}',
  },
  // Slovak
  sk: {
    pickerLabel: 'Vytvárať pomocou',
    pickerLoading: 'Načítavajú sa modely',
    pickerNone: 'Nie je k dispozícii žiadny model',
    pickerDenied: 'Tu si nemôžete vybrať model',
    pickerError: 'Modely sa nepodarilo načítať',
    pickerBlocked: 'Výber modelu je pozastavený',
    pickerUnavailable: 'Nie je k dispozícii',
    pickerCurrent: 'Vybrané',
    pickerNextTurn: 'Platí od vašej ďalšej správy.',
    pickerFootnote:
      'Zapamätané pre vaše ďalšie zostavenia. Správca rozhoduje, ktoré modely môže vaša firma používať.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Druh artefaktu',
    kindSheetTitle: 'Druh',
    kindUnavailable: 'Zatiaľ nie je k dispozícii',
    kindGroupApp: 'Interaktívny',
    kindGroupRead: 'Vytvorené na čítanie alebo odoslanie',
    checkedTitle: 'Skontrolované, kým som povedal „hotovo“',
    checkedPassed: 'Prešlo',
    checkedFailed: 'Neprešlo',
    checkedBlocked: 'Nepodarilo sa spustiť',
    checkedStale: 'Zastarané: artefakt sa po tejto kontrole zmenil',
    checkNow: 'Skontrolovať',
    checkAgain: 'Skontrolovať znova',
    checkedAbsent: 'Zatiaľ neskontrolované',
    checkedScreens: 'Zachytené obrazovky: {count}',
    checkedLimitations: 'Čo táto kontrola nepokryla',
    checkedVersion: 'Verzia {version}',
    skillsLabel: 'Skilly',
    skillsEmpty: 'Zatiaľ žiadne skilly',
    skillsLoading: 'Načítavajú sa skilly',
    skillsError: 'Skilly sa nepodarilo načítať',
    skillsDenied: 'Tu nemôžete používať skilly',
    skillsAttached: 'Používa tento skill',
    imageAttach: 'Pripojiť obrázok',
    imageDrop: 'Presuňte obrázok a začnite od neho',
    imageNotImage: 'Tento súbor nie je obrázok.',
    imageStatus: 'Obrázok {label}: {status}',
  },
  // Slovenian
  sl: {
    pickerLabel: 'Ustvari z',
    pickerLoading: 'Nalaganje modelov',
    pickerNone: 'Noben model ni na voljo',
    pickerDenied: 'Tukaj ne morete izbrati modela',
    pickerError: 'Modelov ni bilo mogoče naložiti',
    pickerBlocked: 'Izbira modela je začasno ustavljena',
    pickerUnavailable: 'Ni na voljo',
    pickerCurrent: 'Izbrano',
    pickerNextTurn: 'Velja od vašega naslednjega sporočila.',
    pickerFootnote:
      'Zapomnjeno za vaše naslednje gradnje. Skrbnik določi, katere modele lahko uporablja vaše podjetje.',
    kindLabel: 'Artefakt',
    kindMenuLabel: 'Vrsta artefakta',
    kindSheetTitle: 'Vrsta',
    kindUnavailable: 'Še ni na voljo',
    kindGroupApp: 'Interaktivno',
    kindGroupRead: 'Narejeno za branje ali pošiljanje',
    checkedTitle: 'Preverjeno, preden sem rekel »končano«',
    checkedPassed: 'Uspešno',
    checkedFailed: 'Neuspešno',
    checkedBlocked: 'Ni bilo mogoče izvesti',
    checkedStale: 'Zastarelo: artefakt se je po tem preverjanju spremenil',
    checkNow: 'Preveri',
    checkAgain: 'Preveri znova',
    checkedAbsent: 'Še ni preverjeno',
    checkedScreens: 'Zajeti zasloni: {count}',
    checkedLimitations: 'Česa to preverjanje ni zajelo',
    checkedVersion: 'Različica {version}',
    skillsLabel: 'Skili',
    skillsEmpty: 'Še ni skilov',
    skillsLoading: 'Nalaganje skilov',
    skillsError: 'Skilov ni bilo mogoče naložiti',
    skillsDenied: 'Tukaj ne morete uporabljati skilov',
    skillsAttached: 'Uporablja ta skil',
    imageAttach: 'Priloži sliko',
    imageDrop: 'Spustite sliko, da začnete z njo',
    imageNotImage: 'Ta datoteka ni slika.',
    imageStatus: 'Slika {label}: {status}',
  },
};

const artifactComposerCopy: NamespaceCatalog<Key> = { en, sv, ...translated };

export default artifactComposerCopy;
